import {
  NetworkStatus,
  OperationVariables,
  QueryHookOptions,
  QueryResult,
  useQuery as useQueryApollo,
} from '@apollo/client';
import { TypedDocumentNode } from '@graphql-typed-document-node/core';
import { DocumentNode } from 'graphql';
import { get } from 'lodash';
import { useCallback, useMemo } from 'react';

type ExtendedOptions = {
  key?: string;
  transform?: any;
  selector?: string;
};

export type DatasourceAvailabilityStatus =
  | 'ready'
  | 'recalculating'
  | 'empty'
  | 'failed'
  | 'unavailable'
  | null;

/**
 * Reads availability fields from datasource envelopes:
 * { queryXxx: { success, status, statusMessage, result } }
 */
function getDatasourceAvailability(data: any): {
  failed: boolean;
  status: DatasourceAvailabilityStatus;
  statusMessage: string | null;
} {
  if (!data || typeof data !== 'object') {
    return { failed: false, status: null, statusMessage: null };
  }

  for (const value of Object.values(data) as any[]) {
    if (!value || typeof value !== 'object' || !('success' in value)) {
      continue;
    }

    const status = (value.status as DatasourceAvailabilityStatus) || null;
    const statusMessage = value.statusMessage || null;

    if (status === 'recalculating' || status === 'empty') {
      return { failed: false, status, statusMessage };
    }

    if (status === 'failed' || value.success === false) {
      return {
        failed: true,
        status: status || 'failed',
        statusMessage,
      };
    }

    if (status) {
      return { failed: false, status, statusMessage };
    }
  }

  return { failed: false, status: null, statusMessage: null };
}

export function useQuery<
  TData = any,
  TVariables extends OperationVariables = OperationVariables
>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: QueryHookOptions<TData, TVariables> & ExtendedOptions
): QueryResult<TData, TVariables> & {
  dataUnavailable?: boolean;
  availabilityStatus?: DatasourceAvailabilityStatus;
  availabilityMessage?: string | null;
  refreshing?: boolean;
} {
  const {
    key,
    transform,
    selector,
    ...options2
  } = options || {};

  // Do not default notifyOnNetworkStatusChange from pollInterval.
  // Enabling it app-wide makes Apollo flip `loading` on every poll and
  // causes non-widget pages (e.g. Data Provider @ 3s) to visually refresh.
  // Callers that need a background-refresh spinner should opt in explicitly.
  const queryResult = useQueryApollo(query, options2);

  const selectAndTransformDataBlock = useCallback(
    (data: TData | undefined, options: ExtendedOptions) => {
      const { transform, selector } = options;

      if (selector && data) {
        data = get(data, selector);
      }

      if (data && typeof transform === 'function') {
        data = transform(data, options);
      }

      return data;
    },
    []
  );

  const data = useMemo<TData | undefined>(
    () => {
      return selectAndTransformDataBlock(queryResult.data, {
        key,
        transform,
        selector,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryResult.data, selectAndTransformDataBlock, selector]
  );

  const availability = useMemo(
    () => getDatasourceAvailability(queryResult.data),
    [queryResult.data]
  );

  const dataUnavailable = useMemo(() => {
    if (!queryResult.data || queryResult.loading) return false;
    // Recalculating / empty are not hard failures.
    if (
      availability.status === 'recalculating' ||
      availability.status === 'empty'
    ) {
      return false;
    }
    return availability.failed;
  }, [availability, queryResult.data, queryResult.loading]);

  const refreshing = !!(
    (queryResult.networkStatus === NetworkStatus.poll ||
      queryResult.networkStatus === NetworkStatus.refetch) &&
    queryResult.data &&
    data &&
    availability.status !== 'recalculating'
  );

  return useMemo(
    () => ({
      ...queryResult,
      key,
      data,
      dataUnavailable,
      availabilityStatus: availability.status,
      availabilityMessage: availability.statusMessage,
      refreshing,
    }),
    [
      availability.status,
      availability.statusMessage,
      data,
      dataUnavailable,
      key,
      queryResult,
      refreshing,
    ]
  );
}
